import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { apiRequest, API_BASE_URL } from "../api";
import "../css/multi-user-dashboard.css";
import AddEventTemplateModal from "../components/AddEventTemplateModal";
import DeployEventTemplateModal from "../components/DeployEventTemplateModal";

const EventTemplateManagement = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [deletingTemplate, setDeletingTemplate] = useState(null);
    const [deletingInstance, setDeletingInstance] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeployModal, setShowDeployModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [refreshList, setRefreshList] = useState(false);
    const [showInstancesModal, setShowInstancesModal] = useState(false);
    const [instances, setInstances] = useState([]);
    
    // Filter and sort state
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [cityFilter, setCityFilter] = useState('');
    const [tourTemplateFilter, setTourTemplateFilter] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    
    const apiUrl = API_BASE_URL;
    const token = localStorage.getItem("token");

    useEffect(() => {
        fetchTemplates();
    }, [refreshList, searchTerm, statusFilter, cityFilter, tourTemplateFilter, sortBy, sortOrder]);

    const fetchTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            // Build query parameters
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (statusFilter !== '') params.append('is_active', statusFilter);
            if (cityFilter) params.append('city', cityFilter);
            if (tourTemplateFilter) params.append('tour_template_id', tourTemplateFilter);
            if (sortBy) params.append('sort_by', sortBy);
            if (sortOrder) params.append('sort_order', sortOrder);

            const url = `${apiUrl}/event-templates?${params.toString()}`;
            console.log(`Fetching: ${url}`);
            
            const data = await apiRequest(url, "GET", null, token);
            setTemplates(data);
        } catch (err) {
            setError("Failed to load templates.");
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setSelectedTemplate(null);
        setShowAddModal(true);
    };

    const handleEdit = (template) => {
        setSelectedTemplate(template);
        setShowAddModal(true);
    };

    const handleDelete = async (templateId) => {
        setDeletingTemplate(templateId);
        try {
            await apiRequest(`${apiUrl}/event-templates/${templateId}`, "DELETE", null, token);
            setStatusMessage("Template deleted successfully!");
            setRefreshList((prev) => !prev);
        } catch (err) {
            setStatusMessage("Failed to delete template. Please try again.");
        } finally {
            setDeletingTemplate(null);
            setTimeout(() => setStatusMessage(null), 3000);
        }
    };

    const handleDeploy = (template) => {
        setSelectedTemplate(template);
        setShowDeployModal(true);
    };

    const handleViewInstances = async (template) => {
        setSelectedTemplate(template);
        setShowInstancesModal(true);
        try {
            const data = await apiRequest(`${apiUrl}/event-templates/${template.id}/instances`, "GET", null, token);
            setInstances(data);
        } catch (err) {
            setInstances([]);
        }
    };

    const handleDeleteInstance = async (templateId, instanceId) => {
        setDeletingInstance(instanceId);
        try {
            await apiRequest(`${apiUrl}/event-templates/${templateId}/instances/${instanceId}`, "DELETE", null, token);
            setStatusMessage("Instance deleted successfully!");
            // Refresh the instances list
            const data = await apiRequest(`${apiUrl}/event-templates/${templateId}/instances`, "GET", null, token);
            setInstances(data);
        } catch (err) {
            setStatusMessage("Failed to delete instance. Please try again.");
        } finally {
            setDeletingInstance(null);
            setTimeout(() => setStatusMessage(null), 3000);
        }
    };

    if (loading) {
        return (
            <div className="dashboard">
                <Sidebar />
                <main className="main-content">
                    <div className="loading">Loading...</div>
                </main>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard">
                <Sidebar />
                <main className="main-content">
                    <div className="error">{error}</div>
                </main>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <Sidebar />
            <main className="main-content">
                <header className="header component">
                    <span className="component-header">Event Templates</span>
                    <button className="btn btn-primary action" onClick={handleAdd}>
                        <i className="fa fa-plus"></i> New Template
                    </button>
                </header>
                <p><a href="/multi-dashboard">Tour Operations Dashboard</a> &gt; Event Templates</p>
                <hr />
                {statusMessage && (
                    <div className={`alert ${statusMessage.includes("Failed") ? "alert-danger" : "alert-success"}`} style={{ marginBottom: '20px' }}>
                        {statusMessage}
                    </div>
                )}
                
                {/* Filter and Sort Controls */}
                <div className="row mb-3">
                    <div className="col-md-2">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="col-md-2">
                        <select
                            className="form-control"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Filter by city..."
                            value={cityFilter}
                            onChange={(e) => setCityFilter(e.target.value)}
                        />
                    </div>
                    <div className="col-md-2">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Tour template ID..."
                            value={tourTemplateFilter}
                            onChange={(e) => setTourTemplateFilter(e.target.value)}
                        />
                    </div>
                    <div className="col-md-1">
                        <select
                            className="form-control"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="name">Name</option>
                            <option value="event_date">Event Date</option>
                            <option value="city">City</option>
                        </select>
                    </div>
                    <div className="col-md-1">
                        <select
                            className="form-control"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                        >
                            <option value="asc">Asc</option>
                            <option value="desc">Desc</option>
                        </select>
                    </div>
                    <div className="col-md-1">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                setSearchTerm('');
                                setStatusFilter('');
                                setCityFilter('');
                                setTourTemplateFilter('');
                                setSortBy('name');
                                setSortOrder('asc');
                            }}
                        >
                            Clear
                        </button>
                    </div>
                </div>
                
                <div className="tours">
                    <table>
                        <thead>
                            <tr>
                                <th>NAME</th>
                                <th>EVENT DATE</th>
                                <th>CITY</th>
                                <th>TOUR TEMPLATE</th>
                                <th>FORM TEMPLATE</th>
                                <th>STATUS</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.length > 0 ? (
                                templates.map((template) => (
                                    <tr key={template.id}>
                                        <td>{template.name}</td>
                                        <td>{template.event_date || "-"}</td>
                                        <td>{template.city || "-"}</td>
                                        <td>{template.tour_template_name || template.tour_template_id || "-"}</td>
                                        <td>{template.form_template_name || template.form_template_id || "-"}</td>
                                        <td>{template.is_active ? "Active" : "Inactive"}</td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <button className="btn btn-sm btn-view" onClick={() => handleViewInstances(template)}>
                                                    View Instances
                                                </button>
                                                <button className="btn btn-sm btn-primary" onClick={() => handleEdit(template)}>
                                                    Edit
                                                </button>
                                                <button className="btn btn-sm btn-secondary" onClick={() => handleDeploy(template)}>
                                                    Deploy
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-danger" 
                                                    onClick={() => handleDelete(template.id)}
                                                    disabled={deletingTemplate === template.id}
                                                >
                                                    {deletingTemplate === template.id ? "Deleting..." : "Delete"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center">No templates found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Placeholders for modals */}
                {showAddModal && (
                    <AddEventTemplateModal
                        eventTemplate={selectedTemplate}
                        onClose={() => setShowAddModal(false)}
                        onSave={() => {
                            setShowAddModal(false);
                            setRefreshList((prev) => !prev);
                        }}
                    />
                )}
                {showDeployModal && (
                    <DeployEventTemplateModal
                        eventTemplate={selectedTemplate}
                        onClose={() => setShowDeployModal(false)}
                        onDeploy={() => {
                            setShowDeployModal(false);
                            setRefreshList((prev) => !prev);
                        }}
                    />
                )}
                {showInstancesModal && (
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 1000,
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: "#fff",
                                padding: "20px",
                                borderRadius: "5px",
                                width: "500px",
                                maxHeight: "90vh",
                                overflowY: "auto",
                            }}
                        >
                            <h4>Linked Event Instances</h4>
                            {statusMessage && (
                                <div className={`alert ${statusMessage.includes("Failed") ? "alert-danger" : "alert-success"}`} style={{ marginBottom: '15px' }}>
                                    {statusMessage}
                                </div>
                            )}
                            <ul>
                                {instances.length > 0 ? (
                                    instances.map((inst) => (
                                        <li key={inst.id} style={{ marginBottom: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <strong>{inst.university_name}</strong> - {inst.tour_name} - {inst.event_name}
                                                    <br />
                                                    <small style={{ color: '#666' }}>
                                                        Template Controlled: {inst.is_template_controlled ? "Yes" : "No"}
                                                    </small>
                                                </div>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleDeleteInstance(selectedTemplate.id, inst.id)}
                                                    disabled={deletingInstance === inst.id}
                                                    style={{ marginLeft: '10px' }}
                                                >
                                                    {deletingInstance === inst.id ? "Deleting..." : "Delete"}
                                                </button>
                                            </div>
                                        </li>
                                    ))
                                ) : (
                                    <li>No instances found.</li>
                                )}
                            </ul>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowInstancesModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default EventTemplateManagement; 